import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ShoppingBag, Save, Info, ArrowLeft, Zap } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { ProductNextSteps } from '../components/website/ProductNextSteps';
import { ProductCmsContent } from '../components/website/ProductCmsContent';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Drape-specific style options
const DRAPE_STYLES = [
  { 
    id: 'ripplefold', 
    name: 'Ripple Fold', 
    description: 'Modern continuous wave pattern with smooth, flowing folds',
    image: '/api/storage/files/apollo-shades/cms/ai-generated/batch/c882e9e9-923e-4d01-982a-952ffb99bbee/28f54a7b-728a-476f-8fa7-243c483d02a4.webp',
    price: 0 
  },
  { 
    id: 'tailored-pleat', 
    name: 'Tailored Pleat', 
    description: 'Clean, crisp pleats for a structured, elegant look',
    image: '/api/storage/files/apollo-shades/apollo-shades/cms/ai-generated/batch/37cd846e-bfd8-46e6-9e24-6c8508a19f77/8f83bf43-d8c3-497b-9ddc-b245001128d0.webp',
    price: 35 
  },
  { 
    id: 'pinch-pleat', 
    name: 'Pinch Pleat', 
    description: 'Classic triple-pinch pleats for traditional elegance',
    image: '/img/catalog/pinch-pleat-benefits.jpg',
    price: 45 
  },
];

// Lining options
const LINING_OPTIONS = [
  { id: 'unlined', name: 'Unlined', description: 'Light filtering, natural drape', price: 0 },
  { id: 'privacy', name: 'Privacy Lining', description: 'Light filtering with added privacy', price: 35 },
  { id: 'blackout', name: 'Blackout Lining', description: '100% light blocking for bedrooms', price: 65 },
  { id: 'thermal', name: 'Thermal Lining', description: 'Insulating layer for energy efficiency', price: 55 },
];

// Opening direction options
const OPENING_OPTIONS = [
  { id: 'center', name: 'Center Opening', description: 'Drapes split and open to both sides', icon: '⟷' },
  { id: 'left', name: 'Open to Left', description: 'Single panel slides to the left', icon: '←' },
  { id: 'right', name: 'Open to Right', description: 'Single panel slides to the right', icon: '→' },
];

// Mount types for drapes (Wall or Ceiling only)
const DRAPE_MOUNT_TYPES = [
  { id: 'wall', name: 'Wall Mount', description: 'Track mounted to wall above window', price: 0 },
  { id: 'ceiling', name: 'Ceiling Mount', description: 'Track recessed or surface mounted to ceiling', price: 25 },
];

export const MotorizedDrapesPage = () => {
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [openSections, setOpenSections] = useState([1]);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    product_type: 'motorized-drapes',
    drape_style: '',
    fabric_material: '',
    fabric_color: '',
    mount_type: 'wall',
    width: 72,
    height: 96,
    lining: 'unlined',
    control_type: 'motorized',
    opening_direction: 'center',
    motor_type: 'battery',
    optional_features: [],
    room_label: '',
    quantity: 1,
  });
  const [price, setPrice] = useState({ unit_price: 0, total_price: 0 });

  const { addToCart, loading: cartLoading } = useCart();
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const response = await axios.get(`${API_URL}/products/motorized-drapes/all`);
        setProductData(response.data);
        if (response.data.materials?.length > 0) {
          setConfig(prev => ({ ...prev, fabric_material: response.data.materials[0].id }));
        }
      } catch (error) {
        console.error('Failed to fetch product data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProductData();
  }, []);

  useEffect(() => {
    const calculatePrice = async () => {
      if (config.fabric_material && config.fabric_color && config.drape_style) {
        try {
          // Custom price calculation for drapes
          const baseConfig = {
            ...config,
            pleat_style: config.drape_style,
            track_type: config.mount_type === 'ceiling' ? 'ceiling-track' : 'wall-track',
            // Required by backend but not applicable to drapes - use defaults
            control_position: 'left',
            bracket_color: 'white',
          };
          const response = await axios.post(`${API_URL}/calculate-price`, baseConfig);
          
          // Add lining and style prices
          const liningPrice = LINING_OPTIONS.find(l => l.id === config.lining)?.price || 0;
          const stylePrice = DRAPE_STYLES.find(s => s.id === config.drape_style)?.price || 0;
          
          const adjustedUnitPrice = response.data.unit_price + liningPrice + stylePrice;
          const adjustedTotalPrice = adjustedUnitPrice * config.quantity;
          
          setPrice({ unit_price: adjustedUnitPrice, total_price: adjustedTotalPrice });
        } catch (error) {
          console.error('Price calculation failed:', error);
        }
      }
    };
    calculatePrice();
  }, [config]);

  const availableColors = useMemo(() => {
    if (!productData || !config.fabric_material) return [];
    return productData.colors.filter(color => color.material_ids.includes(config.fabric_material));
  }, [productData, config.fabric_material]);

  useEffect(() => {
    if (config.fabric_material && availableColors.length > 0) {
      const colorAvailable = availableColors.some(c => c.id === config.fabric_color);
      if (!colorAvailable) {
        setConfig(prev => ({ ...prev, fabric_color: availableColors[0].id }));
      }
    }
  }, [availableColors, config.fabric_material, config.fabric_color]);

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleFeature = (featureId) => {
    setConfig(prev => ({
      ...prev,
      optional_features: prev.optional_features.includes(featureId)
        ? prev.optional_features.filter(f => f !== featureId)
        : [...prev.optional_features, featureId]
    }));
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1: return config.drape_style;
      case 2: return config.fabric_material && config.fabric_color;
      case 3: return config.mount_type;
      case 4: return config.width >= 24 && config.width <= 240 && config.height >= 48 && config.height <= 144;
      case 5: return config.lining;
      case 6: return config.opening_direction;
      default: return true;
    }
  };

  const getStepSummary = (stepId) => {
    switch (stepId) {
      case 1:
        const style = DRAPE_STYLES.find(s => s.id === config.drape_style);
        return style ? style.name : 'Select style';
      case 2:
        const material = productData?.materials?.find(m => m.id === config.fabric_material);
        const color = productData?.colors?.find(c => c.id === config.fabric_color);
        return material && color ? `${material.name} - ${color.name}` : 'Select fabric';
      case 3:
        const mount = DRAPE_MOUNT_TYPES.find(m => m.id === config.mount_type);
        return mount ? mount.name : 'Select mount';
      case 4:
        return `${config.width}" W × ${config.height}" H (Qty: ${config.quantity})`;
      case 5:
        const lining = LINING_OPTIONS.find(l => l.id === config.lining);
        return lining ? lining.name : 'Select lining';
      case 6:
        const opening = OPENING_OPTIONS.find(o => o.id === config.opening_direction);
        return opening ? `Motorized - ${opening.name}` : 'Select control';
      default:
        return '';
    }
  };

  const handleAddToCart = async () => {
    try {
      const cartConfig = {
        ...config,
        pleat_style: config.drape_style,
        track_type: config.mount_type === 'ceiling' ? 'ceiling-track' : 'wall-track',
        // Required by backend but not applicable to drapes - use defaults
        control_position: 'left',
        bracket_color: 'white',
      };
      await addToCart(cartConfig);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleSaveDesign = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save designs');
      return;
    }
    try {
      const saveConfig = {
        ...config,
        pleat_style: config.drape_style,
        track_type: config.mount_type === 'ceiling' ? 'ceiling-track' : 'wall-track',
        // Required by backend but not applicable to drapes - use defaults
        control_position: 'left',
        bracket_color: 'white',
      };
      await axios.post(`${API_URL}/configurations`, saveConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design saved!');
    } catch (error) {
      toast.error('Failed to save design');
    }
  };

  const selectedMaterial = productData?.materials?.find(m => m.id === config.fabric_material);
  const selectedColor = productData?.colors?.find(c => c.id === config.fabric_color);
  const selectedStyle = DRAPE_STYLES.find(s => s.id === config.drape_style);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Product Info Page
  if (!showConfigurator) {
    return (
      <Layout>
        {/* Hero */}
        <section className="relative min-h-[70vh] flex items-center">
          <div className="absolute inset-0">
            <img 
              src="/api/storage/files/apollo-shades/cms/ai-generated/batch/c882e9e9-923e-4d01-982a-952ffb99bbee/e2909b79-63bd-474f-a26b-9e4ac21acac9.webp" 
              alt="Motorized Drapes" 
              className="w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          </div>
          <div className="container-xl relative z-10 pt-20">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
              <Link to="/products" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Products
              </Link>
              <h1 className="text-white mb-6">Motorized Drapes</h1>
              <p className="text-xl text-neutral-300 mb-8">
                Elegant motorized drapery systems for modern interiors with smooth operation and smart home integration.
              </p>
              <Button 
                onClick={() => setShowConfigurator(true)} 
                className="btn-primary !bg-white !text-neutral-900 hover:!bg-neutral-100" 
                data-testid="start-configurator-btn"
              >
                Configure Now <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Style Options Preview */}
        <section className="section-padding">
          <div className="container-xl">
            <div className="text-center mb-12">
              <span className="label-overline text-amber-700">Pleat Styles</span>
              <h2 className="mt-4">Choose Your Style</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {DRAPE_STYLES.map((style) => (
                <div key={style.id} className="group cursor-pointer" onClick={() => { setConfig(prev => ({ ...prev, drape_style: style.id })); setShowConfigurator(true); }}>
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img 
                      src={style.image} 
                      alt={style.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h4 className="text-xl mb-2">{style.name}</h4>
                  <p className="text-neutral-600 text-sm">{style.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-neutral-50">
          <div className="container-xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="label-overline text-amber-700">Features</span>
                <h2 className="mt-4 mb-6">Why Choose Motorized Drapes?</h2>
                <ul className="space-y-4">
                  {[
                    'Whisper-quiet motor operation',
                    'Smart home integration (Alexa, Google, HomeKit)',
                    'Battery or hardwired options',
                    'Custom fabric & lining selections',
                    'Professional installation available',
                    'Wall or ceiling mount options'
                  ].map((benefit) => (
                    <li key={benefit} className="flex items-center gap-3 text-neutral-600">
                      <Check className="w-5 h-5 text-amber-600" /> {benefit}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => setShowConfigurator(true)} className="btn-primary mt-8">
                  Start Configuring
                </Button>
              </div>
              <div>
                <img 
                  src="/img/catalog/motorized-drapes-feature-v2.jpg" 
                  alt="Motorized drapes features" 
                  className="w-full aspect-[4/3] object-cover" 
                />
              </div>
            </div>
          </div>
        </section>
        <ProductCmsContent slug="motorized-drapes" />
        <ProductNextSteps slug="motorized-drapes" name="motorized drapes" />
      </Layout>
    );
  }

  // Configurator
  return (
    <Layout hideFooter>
      <div className="min-h-screen flex justify-center pt-20 bg-neutral-100" data-testid="drapes-configurator">
        <div className="grid lg:grid-cols-2 w-full max-w-7xl">
          {/* Left Panel - Preview */}
          <div className="hidden lg:flex sticky top-20 h-[calc(100vh-5rem)] items-center justify-center p-4 relative overflow-hidden bg-stone-900">
            <div className="relative w-full max-w-xl" data-testid="drapes-preview">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <div className="relative" style={{ aspectRatio: '16/10' }}>
                  <img 
                    src="/api/storage/files/apollo-shades/cms/ai-generated/batch/c882e9e9-923e-4d01-982a-952ffb99bbee/e2909b79-63bd-474f-a26b-9e4ac21acac9.webp"
                    alt="Room preview"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Drapes Visualization */}
                  <div className="absolute inset-0 flex">
                    {/* Left Drape Panel */}
                    <motion.div 
                      className="h-full"
                      style={{ 
                        width: config.opening_direction === 'center' ? '35%' : 
                               config.opening_direction === 'right' ? '70%' : '0%',
                        backgroundColor: selectedColor?.hex || '#e5e5e5',
                        opacity: config.lining === 'blackout' ? 0.95 : 
                                 config.lining === 'unlined' ? 0.7 : 0.85,
                        boxShadow: 'inset -15px 0 30px rgba(0,0,0,0.15)'
                      }}
                      animate={{ width: config.opening_direction === 'center' ? '35%' : config.opening_direction === 'right' ? '70%' : '0%' }}
                      transition={{ duration: 0.5 }}
                    >
                      {/* Pleat lines */}
                      {config.drape_style === 'ripplefold' && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 20px, rgba(0,0,0,0.08) 20px, rgba(0,0,0,0.08) 22px, transparent 22px, transparent 40px)',
                        }} />
                      )}
                      {config.drape_style === 'pinch-pleat' && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 30px, rgba(0,0,0,0.1) 30px, rgba(0,0,0,0.1) 35px, transparent 35px, transparent 60px)',
                        }} />
                      )}
                      {config.drape_style === 'tailored-pleat' && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 25px, rgba(0,0,0,0.06) 25px, rgba(0,0,0,0.06) 27px, transparent 27px, transparent 50px)',
                        }} />
                      )}
                    </motion.div>
                    
                    {/* Gap / Window */}
                    <div className="flex-1" />
                    
                    {/* Right Drape Panel */}
                    <motion.div 
                      className="h-full"
                      style={{ 
                        width: config.opening_direction === 'center' ? '35%' : 
                               config.opening_direction === 'left' ? '70%' : '0%',
                        backgroundColor: selectedColor?.hex || '#e5e5e5',
                        opacity: config.lining === 'blackout' ? 0.95 : 
                                 config.lining === 'unlined' ? 0.7 : 0.85,
                        boxShadow: 'inset 15px 0 30px rgba(0,0,0,0.15)'
                      }}
                      animate={{ width: config.opening_direction === 'center' ? '35%' : config.opening_direction === 'left' ? '70%' : '0%' }}
                      transition={{ duration: 0.5 }}
                    >
                      {/* Pleat lines */}
                      {config.drape_style === 'ripplefold' && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 20px, rgba(0,0,0,0.08) 20px, rgba(0,0,0,0.08) 22px, transparent 22px, transparent 40px)',
                        }} />
                      )}
                      {config.drape_style === 'pinch-pleat' && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 30px, rgba(0,0,0,0.1) 30px, rgba(0,0,0,0.1) 35px, transparent 35px, transparent 60px)',
                        }} />
                      )}
                      {config.drape_style === 'tailored-pleat' && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 25px, rgba(0,0,0,0.06) 25px, rgba(0,0,0,0.06) 27px, transparent 27px, transparent 50px)',
                        }} />
                      )}
                    </motion.div>
                  </div>
                  
                  {/* Track at top */}
                  <div className="absolute top-0 left-0 right-0 h-4 bg-neutral-700 z-10" style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }} />
                  
                  {/* Motor indicator */}
                  <div className="absolute top-0 right-4 z-20">
                    <div className="w-8 h-4 bg-neutral-600 rounded-b flex items-center justify-center gap-1">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Info bar */}
              <div className="mt-4 flex items-center justify-between px-2">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg shadow-inner border border-white/20"
                    style={{ backgroundColor: selectedColor?.hex || '#f5f5f4' }}
                  />
                  <div>
                    <p className="text-white/90 text-sm font-medium">{selectedColor?.name || 'Select Color'}</p>
                    <p className="text-white/50 text-xs">{selectedStyle?.name || 'Select Style'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-mono text-lg">{config.width}" × {config.height}"</p>
                  <p className="text-white/50 text-xs">{config.room_label || 'Custom Size'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Configuration Steps */}
          <div className="bg-white min-h-screen pb-32">
            <div className="p-6 lg:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-serif text-neutral-900">Configure Your Motorized Drapes</h1>
                  <p className="text-neutral-500 mt-1">Customize each option below</p>
                </div>
                <Button variant="ghost" onClick={() => setShowConfigurator(false)} className="text-neutral-500">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </div>

              <div className="space-y-3">
                {/* Step 1: Types & Styles */}
                <Collapsible open={openSections.includes(1)} onOpenChange={() => toggleSection(1)}>
                  <CollapsibleTrigger className="w-full" data-testid="step-1-trigger">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      openSections.includes(1) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isStepComplete(1) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {isStepComplete(1) ? <Check className="w-4 h-4" /> : '1'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-neutral-900">Types & Styles</p>
                          <p className="text-sm text-neutral-500">{getStepSummary(1)}</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(1) ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-4 pb-2 px-2" data-testid="step-1-content">
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Select Pleat Style</Label>
                      <div className="grid gap-3">
                        {DRAPE_STYLES.map((style) => (
                          <button
                            key={style.id}
                            onClick={() => updateConfig('drape_style', style.id)}
                            className={`p-4 border rounded-lg text-left transition-all ${
                              config.drape_style === style.id ? 'border-neutral-900 bg-neutral-50 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                            data-testid={`style-${style.id}`}
                          >
                            <div className="flex gap-4">
                              <img src={style.image} alt={style.name} className="w-20 h-16 object-cover rounded" />
                              <div className="flex-1">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-neutral-900">{style.name}</p>
                                    <p className="text-xs text-neutral-500 mt-1">{style.description}</p>
                                  </div>
                                  {style.price > 0 && (
                                    <span className="font-mono text-xs text-neutral-600">+{formatPrice(style.price)}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Step 2: Fabric Selection */}
                <Collapsible open={openSections.includes(2)} onOpenChange={() => toggleSection(2)}>
                  <CollapsibleTrigger className="w-full" data-testid="step-2-trigger">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      openSections.includes(2) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isStepComplete(2) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {isStepComplete(2) ? <Check className="w-4 h-4" /> : '2'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-neutral-900">Fabric Selection</p>
                          <p className="text-sm text-neutral-500">{getStepSummary(2)}</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(2) ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-4 pb-2 px-2" data-testid="step-2-content">
                      <div className="mb-6">
                        <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Fabric Type</Label>
                        <div className="grid gap-2">
                          {productData?.materials?.map((material) => (
                            <button
                              key={material.id}
                              onClick={() => updateConfig('fabric_material', material.id)}
                              className={`p-3 border rounded-lg text-left transition-all ${
                                config.fabric_material === material.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                              }`}
                              data-testid={`material-${material.id}`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium text-neutral-900 text-sm">{material.name}</p>
                                  <p className="text-xs text-neutral-500">{material.description}</p>
                                </div>
                                <span className="font-mono text-xs text-neutral-600">from {formatPrice(material.base_price)}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      {config.fabric_material && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Color</Label>
                          <div className="flex flex-wrap gap-2">
                            {availableColors.map((color) => (
                              <TooltipProvider key={color.id}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => updateConfig('fabric_color', color.id)}
                                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                        config.fabric_color === color.id ? 'border-neutral-900 scale-110' : 'border-transparent hover:scale-105'
                                      }`}
                                      style={{ backgroundColor: color.hex }}
                                      data-testid={`color-${color.id}`}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent><p>{color.name}</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ))}
                          </div>
                          {selectedColor && <p className="text-xs text-neutral-500 mt-2">Selected: {selectedColor.name}</p>}
                        </motion.div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Step 3: Mount Type */}
                <Collapsible open={openSections.includes(3)} onOpenChange={() => toggleSection(3)}>
                  <CollapsibleTrigger className="w-full" data-testid="step-3-trigger">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      openSections.includes(3) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isStepComplete(3) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {isStepComplete(3) ? <Check className="w-4 h-4" /> : '3'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-neutral-900">Mount Type</p>
                          <p className="text-sm text-neutral-500">{getStepSummary(3)}</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(3) ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-4 pb-2 px-2" data-testid="step-3-content">
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Select Mount</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {DRAPE_MOUNT_TYPES.map((mount) => (
                          <button
                            key={mount.id}
                            onClick={() => updateConfig('mount_type', mount.id)}
                            className={`p-4 border rounded-lg text-center transition-all ${
                              config.mount_type === mount.id ? 'border-neutral-900 bg-neutral-50 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                            data-testid={`mount-${mount.id}`}
                          >
                            <div className="text-3xl mb-2">{mount.id === 'wall' ? '🧱' : '⬆️'}</div>
                            <p className="font-medium text-neutral-900 text-sm">{mount.name}</p>
                            <p className="text-xs text-neutral-500 mt-1">{mount.description}</p>
                            {mount.price > 0 && (
                              <span className="font-mono text-xs text-neutral-600 mt-2 block">+{formatPrice(mount.price)}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Step 4: Size */}
                <Collapsible open={openSections.includes(4)} onOpenChange={() => toggleSection(4)}>
                  <CollapsibleTrigger className="w-full" data-testid="step-4-trigger">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      openSections.includes(4) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isStepComplete(4) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {isStepComplete(4) ? <Check className="w-4 h-4" /> : '4'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-neutral-900">Size</p>
                          <p className="text-sm text-neutral-500">{getStepSummary(4)}</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(4) ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-4 pb-2 px-2" data-testid="step-4-content">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Width (inches)</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={config.width}
                              onChange={(e) => updateConfig('width', parseFloat(e.target.value) || 0)}
                              min={24}
                              max={240}
                              step={0.125}
                              className="text-center h-12"
                              data-testid="width-input"
                            />
                            <span className="text-neutral-400">"</span>
                          </div>
                          <p className="text-xs text-neutral-400 mt-1">24" - 240"</p>
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Height (inches)</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={config.height}
                              onChange={(e) => updateConfig('height', parseFloat(e.target.value) || 0)}
                              min={48}
                              max={144}
                              step={0.125}
                              className="text-center h-12"
                              data-testid="height-input"
                            />
                            <span className="text-neutral-400">"</span>
                          </div>
                          <p className="text-xs text-neutral-400 mt-1">48" - 144"</p>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Quantity</Label>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateConfig('quantity', Math.max(1, config.quantity - 1))}
                            className="w-10 h-10 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50"
                            data-testid="qty-decrease"
                          >-</button>
                          <span className="font-mono text-lg w-8 text-center">{config.quantity}</span>
                          <button
                            onClick={() => updateConfig('quantity', config.quantity + 1)}
                            className="w-10 h-10 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50"
                            data-testid="qty-increase"
                          >+</button>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Step 5: Lining */}
                <Collapsible open={openSections.includes(5)} onOpenChange={() => toggleSection(5)}>
                  <CollapsibleTrigger className="w-full" data-testid="step-5-trigger">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      openSections.includes(5) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isStepComplete(5) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {isStepComplete(5) ? <Check className="w-4 h-4" /> : '5'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-neutral-900">Lining</p>
                          <p className="text-sm text-neutral-500">{getStepSummary(5)}</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(5) ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-4 pb-2 px-2" data-testid="step-5-content">
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Select Lining</Label>
                      <div className="grid gap-2">
                        {LINING_OPTIONS.map((lining) => (
                          <button
                            key={lining.id}
                            onClick={() => updateConfig('lining', lining.id)}
                            className={`p-3 border rounded-lg text-left transition-all ${
                              config.lining === lining.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                            data-testid={`lining-${lining.id}`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-neutral-900 text-sm">{lining.name}</p>
                                <p className="text-xs text-neutral-500">{lining.description}</p>
                              </div>
                              {lining.price > 0 && (
                                <span className="font-mono text-xs text-neutral-600">+{formatPrice(lining.price)}</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Step 6: Control */}
                <Collapsible open={openSections.includes(6)} onOpenChange={() => toggleSection(6)}>
                  <CollapsibleTrigger className="w-full" data-testid="step-6-trigger">
                    <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      openSections.includes(6) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isStepComplete(6) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                        }`}>
                          {isStepComplete(6) ? <Check className="w-4 h-4" /> : '6'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-neutral-900">Control</p>
                          <p className="text-sm text-neutral-500">{getStepSummary(6)}</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(6) ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-4 pb-2 px-2" data-testid="step-6-content">
                      {/* Motorized indicator */}
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                        <Zap className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-neutral-900 text-sm">Motorized Control</p>
                          <p className="text-xs text-neutral-500">Whisper-quiet motor with smart home integration</p>
                        </div>
                      </div>

                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Opening Direction</Label>
                      <div className="grid gap-2">
                        {OPENING_OPTIONS.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => updateConfig('opening_direction', option.id)}
                            className={`p-4 border rounded-lg text-left transition-all ${
                              config.opening_direction === option.id ? 'border-neutral-900 bg-neutral-50 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                            data-testid={`opening-${option.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <span className="text-2xl w-10 text-center">{option.icon}</span>
                              <div>
                                <p className="font-medium text-neutral-900 text-sm">{option.name}</p>
                                <p className="text-xs text-neutral-500">{option.description}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Motor Type */}
                      <div className="mt-4">
                        <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Motor Type</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => updateConfig('motor_type', 'battery')}
                            className={`p-3 border rounded-lg text-center transition-all ${
                              config.motor_type === 'battery' ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                          >
                            <p className="font-medium text-neutral-900 text-sm">Battery</p>
                            <p className="text-xs text-neutral-500">Rechargeable</p>
                          </button>
                          <button
                            onClick={() => updateConfig('motor_type', 'hardwired')}
                            className={`p-3 border rounded-lg text-center transition-all ${
                              config.motor_type === 'hardwired' ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                          >
                            <p className="font-medium text-neutral-900 text-sm">Hardwired</p>
                            <p className="text-xs text-neutral-500">Professional install</p>
                          </button>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Room Label */}
              <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
                <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Room Label (Optional)</Label>
                <Input
                  value={config.room_label}
                  onChange={(e) => updateConfig('room_label', e.target.value)}
                  placeholder="e.g., Living Room, Master Bedroom"
                  className="h-10"
                  data-testid="room-label-input"
                />
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="fixed bottom-0 left-0 lg:left-1/2 right-0 bg-white border-t border-neutral-200 px-6 py-4 z-20">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Total</p>
                  <p className="font-mono text-2xl font-medium text-neutral-900" data-testid="price-display">
                    {formatPrice(price.total_price)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveDesign}
                    variant="outline"
                    className="h-12 px-4"
                    data-testid="save-design-button"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    onClick={handleAddToCart}
                    disabled={!config.drape_style || !config.fabric_material || !config.fabric_color || cartLoading}
                    className="btn-primary h-12 px-6"
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {cartLoading ? 'Adding...' : 'Add to Cart'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
